import { reorder } from "@atlaskit/pragmatic-drag-and-drop/reorder";
import { Modal, Table } from "antd";
import DraggableRow from "components/organisms/DragAndDrop/DraggableRow";
import DragHandle from "components/organisms/DragAndDrop/DragHandle";
import { useCustomPlanUpdate } from "hooks/CustomPlan/useCustomPlanUpdate";
import useAlert from "hooks/useAlert";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReorderMeasuresFooter from "./ReoderMeasuresFooter";
import styles from "./ReoderMeasuresModal.module.scss";

interface ReorderMeasuresModalProps {
    modalIsOpen: boolean;
    onCancel: () => void;
    dataSource: CarbonReductionMeasure[];
    planStartYear: number;
    planEndYear: number;
}

function ReorderMeasuresModal({
    modalIsOpen,
    onCancel,
    dataSource,
    planStartYear,
    planEndYear,
}: ReorderMeasuresModalProps) {
    const buildingModelUid = useBuildingModelUid();
    const { buildingModelCarbonReductionPlanId } = useParams();

    if (!buildingModelCarbonReductionPlanId) throw new Error("Missing buildingModelCarbonReductionPlanId");

    const { updateCustomPlanMeasures, isLoading } = useCustomPlanUpdate({
        buildingModelUid,
        buildingModelCarbonReductionPlanId,
    });

    const dataSorted = dataSource.sort((a, b) => a.yearApplied - b.yearApplied);

    const [data, setData] = useState(dataSorted);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>("");
    const [flashedYear, setFlashedYear] = useState<number | null>(null);
    const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const alert = useAlert();

    const triggerFlash = (year: number) => {
        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        setFlashedYear(year);
        flashTimeoutRef.current = setTimeout(() => setFlashedYear(null), 700);
    };

    useEffect(() => {
        if (modalIsOpen) {
            setData([...dataSource].sort((a, b) => a.yearApplied - b.yearApplied));
            setEditingIndex(null);
            setEditingValue("");
        }
    }, [modalIsOpen]);

    const MIN_YEAR = planStartYear + 1;
    const MAX_YEAR = planEndYear;

    const commitEdit = (index: number, value: string) => {
        const newYear = parseInt(value, 10);
        const existingYears = data.map((d) => d.yearApplied).filter((_, i) => i !== index);

        if (Number.isNaN(newYear) || newYear < MIN_YEAR || newYear > MAX_YEAR || existingYears.includes(newYear)) {
            if (Number.isNaN(newYear)) {
                alert.error("Please enter a valid year.");
            } else if (existingYears.includes(newYear)) {
                alert.error(`Year ${newYear} is already used by another measure.`);
            } else {
                alert.error(`Year must be between ${MIN_YEAR} and ${MAX_YEAR}.`);
            }
            setEditingIndex(null);
            return;
        }

        const updated = data
            .map((item, i) => (i === index ? { ...item, yearApplied: newYear } : item))
            .sort((a, b) => a.yearApplied - b.yearApplied);
        setData(updated);
        setEditingIndex(null);
        triggerFlash(newYear);
    };

    const modalSubmitHandler = () => {
        updateCustomPlanMeasures("reorder", {
            reorderedMeasures: data,
            onSuccess: onCancel,
        });
    };

    const columns = [
        {
            title: "",
            dataIndex: "sort",
            width: 50,
            render: () => <DragHandle />,
        },
        {
            title: "Measure Type",
            dataIndex: "carbonReductionMeasureType",
            key: "carbonReductionMeasureType",
        },
        {
            title: "Year Applied",
            dataIndex: "yearApplied",
            key: "yearApplied",
            render: (year: number, _: CarbonReductionMeasure, index: number) => {
                if (editingIndex === index) {
                    return (
                        <input
                            type="number"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={(e) => commitEdit(index, e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") commitEdit(index, editingValue);
                                if (e.key === "Escape") setEditingIndex(null);
                            }}
                            style={{ width: 70 }}
                        />
                    );
                }
                return (
                    <button
                        type="button"
                        style={{ cursor: "pointer", background: "none", border: "none", padding: 0 }}
                        onClick={() => {
                            setEditingIndex(index);
                            setEditingValue(String(year));
                        }}
                    >
                        {year}
                    </button>
                );
            },
        },
    ];

    const handleDrop = (sourceIndex: number, destinationIndex: number) => {
        const reordered = reorder({
            list: data,
            startIndex: sourceIndex,
            finishIndex: destinationIndex,
        });

        const isFirstIndex = destinationIndex === 0;
        const newYear = isFirstIndex
            ? reordered[destinationIndex + 1].yearApplied - 1
            : reordered[destinationIndex - 1].yearApplied + 1;

        if (newYear < MIN_YEAR || newYear > MAX_YEAR) {
            return;
        }

        reordered[destinationIndex] = {
            ...reordered[destinationIndex],
            yearApplied: newYear,
        };

        // This makes sure there are no duplicate years, by adding one year to following conflicted years
        for (let i = destinationIndex + 1; i < reordered.length; i++) {
            if (reordered[i].yearApplied <= reordered[i - 1].yearApplied) {
                reordered[i] = {
                    ...reordered[i],
                    yearApplied: reordered[i - 1].yearApplied + 1,
                };
            }
        }

        setData(reordered);
        triggerFlash(reordered[destinationIndex].yearApplied);
    };

    return (
        <Modal
            open={modalIsOpen}
            onCancel={onCancel}
            title="Reorder measures"
            className={styles.modalContainer}
            footer={
                <ReorderMeasuresFooter onSave={() => modalSubmitHandler()} onCancel={onCancel} isLoading={isLoading} />
            }
        >
            <div>
                <div className={styles.firstHeader}>
                    <p>
                        Drag and drop measures to change the implementation year, or click on a year to edit it
                        directly.
                        <br />
                        <br />
                        The measure will be implemented the year following the previous measure.
                    </p>
                </div>

                <div style={{ maxHeight: "400px", overflow: "auto" }}>
                    {/* TODO - This is currently using antd Table component -  update to our own Table component */}
                    <Table
                        dataSource={data}
                        columns={columns}
                        pagination={false}
                        // rowKey="row"
                        components={{
                            body: {
                                row: DraggableRow,
                            },
                        }}
                        // onRow required to pass props to Draggable row
                        onRow={(record, index) =>
                            ({
                                index,
                                handleDrop,
                                "data-flashing": record.yearApplied === flashedYear ? "true" : undefined,
                            }) as any
                        }
                    />
                </div>
            </div>
        </Modal>
    );
}

export default ReorderMeasuresModal;
